import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal, WritableSignal, computed, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter, finalize } from 'rxjs/operators';
import { GettingStartedService } from '../getting-started.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Steps, StepSequence } from '../getting-started.contants';
import { SPIN_STYLES, SpinInputModel } from 'countable@model';
import { CommonModule } from '@angular/common';
import { StatusPanelComponent } from '../status-panel/status-panel.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';

@Component({
  selector: 'app-onboarding-status',
  templateUrl: './onboarding-status.component.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CommonModule, StatusPanelComponent, MatProgressBarModule
  ],
  styleUrl: './onboarding-status.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true
})
export class OnboardingStatusComponent implements OnInit {

  onboardingStatus: WritableSignal<string> = signal<string>('');
  destroyRef = inject(DestroyRef);
  stepsOrder = [
    'FIRM',
    'TEAM',
    'CLIENT',
    'COMPONENTS',
    'ENGAGEMENT',
  ];
  readonly steps = Steps;
  totalSteps = computed(() => this.steps.length);
  currentStep: WritableSignal<number> = signal<number>(1);
  showPanel: WritableSignal<boolean> = signal<boolean>(false);
  hideTimeout: any;
  public spinnerConfig: WritableSignal<SpinInputModel> = signal<SpinInputModel>(
    new SpinInputModel(null, SPIN_STYLES.BLUR_WITH_SPIN_LINE, false)
  );
  private spinCount: number = 0;
  isInsideGettingStartedPanel: WritableSignal<boolean> = signal<boolean>(false);
  isDrawerOpened: WritableSignal<boolean> = signal<boolean>(true);
  drawerStatus: WritableSignal<boolean> = signal<boolean>(true);
  isDrawerExpanded = computed(() => this.isDrawerOpened() && this.drawerStatus());
  completedSteps: WritableSignal<number> = signal<number>(0);
  progressValue = computed(() => (this.completedSteps() / this.totalSteps()) * 100);

  constructor(
    private gettingStartedService: GettingStartedService,
    private router: Router
  ) { }



  ngOnInit(): void {
    this.fetchOnboardingStatus();
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
      .subscribe((event: NavigationEnd) => {
        // Check if the current url is under /getting-started
        if (event.urlAfterRedirects.startsWith('/getting-started')) {
          this.fetchOnboardingStatus();
        }
      });
      this.isInsideGettingStartedPanel.set(this.router.url.startsWith('/getting-started'));     
      this.gettingStartedService.dashboardDrawerStatus$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((status) => {
        this.isDrawerOpened.set(!status);
      })
      this.gettingStartedService.onboardingDrawerStatus$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((status) => {
        this.drawerStatus.set(!status);
      })
  }

  fetchOnboardingStatus() {
    this.updateSpinnerConfig(true);
    this.gettingStartedService.getFirmOnboardingStatus()
      .pipe(finalize(() => this.updateSpinnerConfig(false)), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.onboardingStatus.set(res.data);
          // find the index of the current step
          const currentIndex = this.stepsOrder.findIndex((step) => step === this.onboardingStatus());
          this.currentStep.set(currentIndex + 1);
          const stepIndex = StepSequence[res.data as keyof typeof StepSequence];
          this.completedSteps.set(stepIndex);
        },
        error: (err) => {
          console.error("Error fetching onboarding status:", err);
        }
      });
  }

  showGetStartedPanel() {
    this.showPanel.set(true);
    clearTimeout(this.hideTimeout);
  }

  hideGetStartedPanelWithDelay() {
    this.hideTimeout = setTimeout(() => {
      this.showPanel.set(false);
    }, 500);
  }

  hideGetStartedPanel() {
    this.showPanel.set(false);
  }

  cancelHide() {
    clearTimeout(this.hideTimeout);
  }

  private updateSpinnerConfig(value: boolean) {
    value ? this.spinCount++ : (this.spinCount = Math.max(0, this.spinCount - 1));
    this.spinnerConfig.update((config) => ({ ...config, isSpinShown: this.spinCount > 0 }));
  }
}
